import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { axiosInstance, API } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, Receipt, Search, X, Eye } from "lucide-react";
import { saveNotaOffline } from "../utils/offlineDb";

const InputNota = ({ user }) => {
  const [nota, setNota] = useState([]);
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentNota, setCurrentNota] = useState(null);
  const [selectedNota, setSelectedNota] = useState(null);
  
  // Filter bulan untuk role user
  const currentDate = new Date();
  const [filterBulan, setFilterBulan] = useState({
    tahun: currentDate.getFullYear(),
    bulan: currentDate.getMonth() + 1,
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  
  const bulanOptions = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];
  
  // Tahun dari 2025 sampai 2035 (sama seperti di form nota)
  const tahunOptions = Array.from({ length: 11 }, (_, i) => 2025 + i);
  
  // Reset page saat filter bulan berubah
  useEffect(() => {
    setCurrentPage(1);
  }, [filterBulan]);
  
  // Form data untuk nota header
  const [formData, setFormData] = useState({
    nomor_nota: "",
    tanggal: new Date().toISOString().split("T")[0],
    counter_id: user?.counter_id || "",
    nama_sales: "",
  });

  // Items dalam nota (dynamic rows)
  const [items, setItems] = useState([{
    id: Date.now(),
    barcode: "",
    nama_barang: "",
    satuan: "",
    harga_satuan: 0,
    jumlah: "",
    diskon1: 0,
    diskon2: 0,
    sub_total: 0,
    total: 0,
    loading: false,
  }]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [notaRes, countersRes] = await Promise.all([
        axiosInstance.get(`${API}/nota`),
        axiosInstance.get(`${API}/counters`),
      ]);

      setNota(notaRes.data);
      setCounters(countersRes.data);
    } catch (error) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  // Lookup product by barcode
  const handleBarcodeChange = async (index, barcode) => {
    const newItems = [...items];
    newItems[index].barcode = barcode;
    newItems[index].loading = true;
    setItems(newItems);
    
    if (barcode.length < 3) {
      newItems[index].nama_barang = "";
      newItems[index].loading = false;
      setItems(newItems);
      return;
    }

    try {
      const response = await axiosInstance.get(`${API}/produk/barcode/${barcode}`);
      const produk = response.data;
      
      // Determine which price to use based on counter type
      let hargaToUse = produk.harga;
      if (formData.counter_id) {
        const selectedCounter = counters.find(c => c.id === formData.counter_id);
        if (selectedCounter && selectedCounter.tipe_harga === "khusus") {
          hargaToUse = produk.harga_khusus || produk.harga;
        }
      }
      
      newItems[index] = {
        ...newItems[index],
        nama_barang: produk.nama,
        satuan: produk.satuan || "PCS", // Ambil satuan dari produk
        harga_satuan: hargaToUse,
        diskon1: produk.diskon1 || 0,
        diskon2: produk.diskon2 || 0,
        loading: false,
      };
      setItems(newItems);
      calculateItem(index, newItems);
      toast.success(`Produk ditemukan: ${produk.nama}`, { duration: 2000 });
    } catch (error) {
      newItems[index].nama_barang = "";
      newItems[index].loading = false;
      setItems(newItems);
      if (error.response?.status === 404) {
        toast.error("Produk tidak ditemukan");
      }
    }
  };

  // Calculate item total
  const calculateItem = (index, itemsArray = items) => {
    const item = itemsArray[index];
    const jumlah = parseFloat(item.jumlah) || 0;
    const sub_total = item.harga_satuan * jumlah;
    const after_diskon1 = sub_total * (1 - item.diskon1 / 100);
    const total = after_diskon1 * (1 - item.diskon2 / 100);
    
    const newItems = [...itemsArray];
    newItems[index].sub_total = sub_total;
    newItems[index].total = total;
    setItems(newItems);
  };

  // Add new item row
  const addItemRow = () => {
    setItems([...items, {
      id: Date.now(),
      barcode: "",
      nama_barang: "",
      satuan: "",
      harga_satuan: 0,
      jumlah: "",
      diskon1: 0,
      diskon2: 0,
      sub_total: 0,
      total: 0,
      loading: false,
    }]);
  };

  // Remove item row
  const removeItemRow = (index) => {
    if (items.length === 1) {
      toast.error("Minimal harus ada 1 item");
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // Calculate sub total (sum of all items)
  const getSubTotal = () => {
    return items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  // Calculate final total
  const getTotalNota = () => {
    return getSubTotal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate items
    const validItems = items.filter(item => item.barcode && item.nama_barang);
    if (validItems.length === 0) {
      toast.error("Minimal harus ada 1 item yang valid");
      return;
    }

    const payload = {
      ...formData,
      items: validItems.map(item => ({
        barcode: item.barcode,
        jumlah: parseInt(item.jumlah),
        diskon1: parseFloat(item.diskon1),
        diskon2: parseFloat(item.diskon2),
      }))
    };

    try {
      if (editMode) {
        // Edit mode always requires online connection
        await axiosInstance.put(`${API}/nota/${currentNota.id}`, payload);
        toast.success("Nota berhasil diperbarui dan tersimpan di database");
      } else {
        // For new nota, try online first, fallback to offline
        if (navigator.onLine) {
          try {
            await axiosInstance.post(`${API}/nota`, payload);
            toast.success("✅ Nota berhasil tersimpan ke database", { duration: 4000 });
          } catch (error) {
            // If online but API fails, save offline
            await saveNotaOffline(payload);
            toast.warning("⚠️ Nota tersimpan offline. Akan otomatis dikirim saat online", { duration: 5000 });
          }
        } else {
          // If offline, save to IndexedDB
          await saveNotaOffline(payload);
          toast.info("📱 Mode Offline: Nota tersimpan di perangkat. Akan otomatis dikirim ke database saat online", { duration: 5000 });
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operasi gagal");
    }
  };

  const handleEdit = (notaItem) => {
    setCurrentNota(notaItem);
    setFormData({
      nomor_nota: notaItem.nomor_nota,
      tanggal: notaItem.tanggal,
      counter_id: notaItem.counter_id,
      nama_sales: notaItem.nama_sales,
    });
    
    // Load items
    const loadedItems = notaItem.items.map(item => ({
      id: item.item_id,
      barcode: item.barcode,
      nama_barang: item.nama_barang,
      satuan: item.satuan,
      harga_satuan: item.harga_satuan,
      jumlah: item.jumlah,
      diskon1: item.diskon1,
      diskon2: item.diskon2,
      sub_total: item.sub_total,
      total: item.total,
      loading: false,
    }));
    
    setItems(loadedItems);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin ingin menghapus nota ini?")) return;

    try {
      await axiosInstance.delete(`${API}/nota/${id}`);
      toast.success("Nota berhasil dihapus");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hapus gagal");
    }
  };

  const handleViewDetail = (notaItem) => {
    setSelectedNota(notaItem);
    setDetailDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      nomor_nota: "",
      tanggal: new Date().toISOString().split("T")[0],
      counter_id: user?.counter_id || "",
      nama_sales: "",
    });
    setItems([{
      id: Date.now(),
      barcode: "",
      nama_barang: "",
      satuan: "",
      harga_satuan: 0,
      jumlah: "",
      diskon1: 0,
      diskon2: 0,
      sub_total: 0,
      total: 0,
      loading: false,
    }]);
    setEditMode(false);
    setCurrentNota(null);
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Filter nota berdasarkan bulan (untuk role user) dan urutkan berdasarkan tanggal (terbaru dulu)
  const getFilteredNota = () => {
    let filtered;
    
    if (user?.role !== 'user') {
      filtered = [...nota]; // Admin melihat semua
    } else {
      filtered = nota.filter(n => {
        const notaDate = new Date(n.tanggal);
        const notaBulan = notaDate.getMonth() + 1;
        const notaTahun = notaDate.getFullYear();
        
        return notaBulan === filterBulan.bulan && notaTahun === filterBulan.tahun;
      });
    }
    
    // Urutkan berdasarkan tanggal nota (terbaru dulu)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.tanggal);
      const dateB = new Date(b.tanggal);
      return dateB - dateA; // Descending (terbaru dulu)
    });
  };

  const filteredNota = getFilteredNota();

  // Pagination logic
  const totalPages = Math.ceil(filteredNota.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNota = filteredNota.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate summary berdasarkan filtered data (SEMUA data, bukan hanya halaman saat ini)
  const totalNota = filteredNota.length;
  const totalItems = filteredNota.reduce((sum, n) => sum + (n.items?.length || 0), 0);
  const totalNilai = filteredNota.reduce((sum, n) => sum + (n.total_nota || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="back-button">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src="/logo-dumont.jpg" alt="Dumont" className="h-12 w-auto" />
              <div className="border-l border-gray-300 pl-3">
                <h1 className="text-2xl font-bold text-gray-800">Input Nota</h1>
                <p className="text-sm text-gray-600">
                  {user?.role === "admin" ? "Kelola transaksi penjualan harian" : "Input nota - 1 nota bisa banyak item"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Bulan - hanya untuk role user */}
        {user?.role === 'user' && (
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Filter Bulan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Tahun</Label>
                  <Select
                    value={filterBulan.tahun.toString()}
                    onValueChange={(value) => setFilterBulan({ ...filterBulan, tahun: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tahunOptions.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Bulan</Label>
                  <Select
                    value={filterBulan.bulan.toString()}
                    onValueChange={(value) => setFilterBulan({ ...filterBulan, bulan: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {bulanOptions.map((month) => (
                        <SelectItem key={month.value} value={month.value.toString()}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        {filteredNota.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
              <CardContent className="pt-6">
                <p className="text-sm text-blue-100 mb-1">Total Nota</p>
                <p className="text-3xl font-bold">{totalNota}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-teal-600 text-white">
              <CardContent className="pt-6">
                <p className="text-sm text-green-100 mb-1">Total Items Barang</p>
                <p className="text-3xl font-bold">{totalItems}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-pink-600 text-white">
              <CardContent className="pt-6">
                <p className="text-sm text-purple-100 mb-1">Total Nilai Nota</p>
                <p className="text-2xl font-bold">{formatRupiah(totalNilai)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">Daftar Nota</CardTitle>
              <Button
                data-testid="add-nota-button"
                onClick={resetForm}
                onClickCapture={() => setDialogOpen(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Nota Baru
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-600">Memuat...</div>
            ) : filteredNota.length === 0 ? (
              <div className="text-center py-8 text-gray-600">
                {user?.role === 'user' 
                  ? `Belum ada data nota untuk ${bulanOptions.find(b => b.value === filterBulan.bulan)?.label} ${filterBulan.tahun}` 
                  : "Belum ada data nota"}
              </div>
            ) : (
              <>
                {/* Info Pagination */}
                <div className="mb-4 text-sm text-gray-600">
                  Menampilkan {startIndex + 1} - {Math.min(endIndex, filteredNota.length)} dari {filteredNota.length} nota
                  {totalPages > 1 && ` (Halaman ${currentPage} dari ${totalPages})`}
                </div>

                {/* Mobile View - Card Layout */}
                <div className="md:hidden space-y-3">
                  {paginatedNota.map((notaItem) => (
                    <Card key={notaItem.id} className="border-2 hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Header: Nomor Nota & Tanggal */}
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="text-xs text-gray-500">No. Nota</div>
                              <div className="text-base font-bold text-blue-600">{notaItem.nomor_nota}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">Tanggal</div>
                              <div className="text-sm font-medium">{notaItem.tanggal}</div>
                            </div>
                          </div>

                          {/* Counter & Sales */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <div className="text-xs text-gray-500">Counter</div>
                              <div className="font-medium">{notaItem.nama_counter}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500">Sales</div>
                              <div className="font-medium">{notaItem.nama_sales || "-"}</div>
                            </div>
                          </div>

                          {/* Items & Total */}
                          <div className="bg-green-50 p-3 rounded-md">
                            <div className="flex justify-between items-center">
                              <div>
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                  {notaItem.items?.length || 0} item
                                </span>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-gray-600">Total Nota</div>
                                <div className="text-lg font-bold text-green-700">
                                  {formatRupiah(notaItem.total_nota)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 pt-2 border-t">
                            <Button
                              size="sm"
                              onClick={() => handleViewDetail(notaItem)}
                              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Lihat
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleEdit(notaItem)}
                              data-testid={`edit-nota-${notaItem.id}`}
                              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleDelete(notaItem.id)}
                              data-testid={`delete-nota-${notaItem.id}`}
                              variant="destructive"
                              className="flex-1"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Hapus
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Desktop View - Table Layout */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>No. Nota</TableHead>
                        <TableHead>Tanggal</TableHead>
                        <TableHead>Counter</TableHead>
                        <TableHead>Sales</TableHead>
                        <TableHead className="text-center">Jumlah Item</TableHead>
                        <TableHead className="text-right bg-green-50">Total Nota</TableHead>
                        <TableHead className="text-right">Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedNota.map((notaItem) => (
                        <TableRow key={notaItem.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-blue-600">{notaItem.nomor_nota}</TableCell>
                          <TableCell>{notaItem.tanggal}</TableCell>
                          <TableCell className="font-medium">{notaItem.nama_counter}</TableCell>
                          <TableCell>{notaItem.nama_sales}</TableCell>
                          <TableCell className="text-center">
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                              {notaItem.items?.length || 0} item
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-700 bg-green-50">
                            {formatRupiah(notaItem.total_nota)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetail(notaItem)}
                                className="hover:bg-purple-50"
                                title="Lihat Detail"
                              >
                                <Eye className="w-4 h-4 text-purple-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(notaItem)}
                                data-testid={`edit-nota-${notaItem.id}`}
                                className="hover:bg-blue-50"
                                title="Edit Nota"
                              >
                                <Pencil className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(notaItem.id)}
                                data-testid={`delete-nota-${notaItem.id}`}
                                className="hover:bg-red-50"
                                title="Hapus Nota"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3"
                    >
                      ← Sebelumnya
                    </Button>
                    
                    <div className="flex gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        // Show first page, last page, current page, and pages around current
                        const showPage = 
                          page === 1 || 
                          page === totalPages || 
                          (page >= currentPage - 1 && page <= currentPage + 1);
                        
                        const showEllipsis = 
                          (page === currentPage - 2 && currentPage > 3) ||
                          (page === currentPage + 2 && currentPage < totalPages - 2);
                        
                        if (showEllipsis) {
                          return <span key={page} className="px-2">...</span>;
                        }
                        
                        if (!showPage) return null;
                        
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className={currentPage === page 
                              ? "bg-blue-600 text-white hover:bg-blue-700" 
                              : ""}
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3"
                    >
                      Berikutnya →
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Total Bulan Ini - hanya untuk role user */}
        {user?.role === 'user' && filteredNota.length > 0 && (
          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-teal-600 text-white mt-6">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-green-100 mb-2">
                  TOTAL {bulanOptions.find(b => b.value === filterBulan.bulan)?.label.toUpperCase()} {filterBulan.tahun}
                </p>
                <p className="text-4xl font-bold mb-2">{formatRupiah(totalNilai)}</p>
                <p className="text-sm text-green-100">
                  {totalNota} nota • {totalItems} items barang
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dialog Tambah/Edit Nota */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">{editMode ? "Edit Nota" : "Tambah Nota Baru"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nota Header */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="nomor_nota">Nomor Nota *</Label>
                  <Input
                    id="nomor_nota"
                    value={formData.nomor_nota}
                    onChange={(e) => setFormData({ ...formData, nomor_nota: e.target.value })}
                    placeholder="INV-001"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tanggal">Tanggal *</Label>
                  <Input
                    id="tanggal"
                    type="date"
                    value={formData.tanggal}
                    onChange={(e) => setFormData({ ...formData, tanggal: e.target.value })}
                    min="2025-01-01"
                    max="2035-12-31"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="counter">Counter *</Label>
                  <Select
                    value={formData.counter_id || undefined}
                    onValueChange={(value) => setFormData({ ...formData, counter_id: value })}
                    disabled={user?.role !== "admin" && user?.counter_id}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih counter" />
                    </SelectTrigger>
                    <SelectContent>
                      {counters.map((counter) => (
                        <SelectItem key={counter.id} value={counter.id}>
                          {counter.nama_counter}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nama_sales">Nama Sales * (Wajib di-isi)</Label>
                  <Input
                    id="nama_sales"
                    value={formData.nama_sales}
                    onChange={(e) => setFormData({ ...formData, nama_sales: e.target.value })}
                    placeholder="Nama sales wajib diisi"
                    required
                  />
                </div>
              </div>

              {/* Items Table/Cards */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-lg font-semibold">Items Barang</Label>
                  <Button
                    type="button"
                    onClick={addItemRow}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Tambah Item
                  </Button>
                </div>

                {/* Mobile View - Card Layout */}
                <div className="md:hidden space-y-4">
                  {items.map((item, index) => (
                    <Card key={item.id} className="border-2">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <span className="text-sm font-semibold text-gray-700">Item #{index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItemRow(index)}
                            className="hover:bg-red-50 -mt-2 -mr-2"
                          >
                            <X className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                        
                        <div className="space-y-3">
                          {/* Barcode */}
                          <div>
                            <Label className="text-xs text-gray-600">Barcode *</Label>
                            <div className="relative mt-1">
                              <Input
                                value={item.barcode}
                                onChange={(e) => handleBarcodeChange(index, e.target.value)}
                                placeholder="Scan atau ketik barcode"
                                className="pr-8 text-base h-11"
                              />
                              {item.loading && (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Nama Barang */}
                          {item.nama_barang && (
                            <div className="bg-blue-50 p-3 rounded-md">
                              <Label className="text-xs text-gray-600">Nama Barang</Label>
                              <div className="text-sm font-medium text-gray-900 mt-1 break-words">
                                {item.nama_barang}
                              </div>
                              <div className="flex justify-between mt-2 text-xs text-gray-600">
                                <span>Satuan: {item.satuan || "-"}</span>
                                <span className="font-semibold text-blue-600">
                                  {item.harga_satuan ? formatRupiah(item.harga_satuan) : "-"}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Qty and Discounts */}
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs text-gray-600">Qty *</Label>
                              <Input
                                type="number"
                                min="1"
                                value={item.jumlah}
                                onChange={(e) => {
                                  const newItems = [...items];
                                  newItems[index].jumlah = e.target.value === "" ? "" : parseInt(e.target.value) || "";
                                  setItems(newItems);
                                  calculateItem(index, newItems);
                                }}
                                placeholder="0"
                                className="mt-1 text-base h-11"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600">Disc1 (%)</Label>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={item.diskon1}
                                onChange={(e) => {
                                  const newItems = [...items];
                                  newItems[index].diskon1 = parseFloat(e.target.value) || 0;
                                  setItems(newItems);
                                  calculateItem(index, newItems);
                                }}
                                className="mt-1 text-base h-11"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600">Disc2 (%)</Label>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={item.diskon2}
                                onChange={(e) => {
                                  const newItems = [...items];
                                  newItems[index].diskon2 = parseFloat(e.target.value) || 0;
                                  setItems(newItems);
                                  calculateItem(index, newItems);
                                }}
                                className="mt-1 text-base h-11"
                              />
                            </div>
                          </div>

                          {/* Total */}
                          {item.total > 0 && (
                            <div className="bg-green-50 p-3 rounded-md flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Total Item:</span>
                              <span className="text-lg font-bold text-green-700">
                                {formatRupiah(item.total)}
                              </span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Desktop View - Table Layout */}
                <div className="hidden md:block border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-100">
                        <TableHead className="min-w-[120px]">Barcode *</TableHead>
                        <TableHead className="min-w-[250px]">Nama Barang</TableHead>
                        <TableHead className="w-[80px]">Satuan</TableHead>
                        <TableHead className="w-[120px] text-right">Harga</TableHead>
                        <TableHead className="w-[80px]">Qty *</TableHead>
                        <TableHead className="w-[90px]">Disc1(%)</TableHead>
                        <TableHead className="w-[90px]">Disc2(%)</TableHead>
                        <TableHead className="w-[140px] text-right">Total</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="relative">
                              <Input
                                value={item.barcode}
                                onChange={(e) => handleBarcodeChange(index, e.target.value)}
                                placeholder="Scan/ketik"
                                className="pr-8 min-w-[110px]"
                              />
                              {item.loading && (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                  <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm max-w-[250px] break-words">
                              {item.nama_barang || <span className="text-gray-400">-</span>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{item.satuan || "-"}</div>
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {item.harga_satuan ? formatRupiah(item.harga_satuan) : "-"}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              value={item.jumlah}
                              onChange={(e) => {
                                const newItems = [...items];
                                newItems[index].jumlah = e.target.value === "" ? "" : parseInt(e.target.value) || "";
                                setItems(newItems);
                                calculateItem(index, newItems);
                              }}
                              placeholder="0"
                              className="w-20"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={item.diskon1}
                              onChange={(e) => {
                                const newItems = [...items];
                                newItems[index].diskon1 = parseFloat(e.target.value) || 0;
                                setItems(newItems);
                                calculateItem(index, newItems);
                              }}
                              className="w-20"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={item.diskon2}
                              onChange={(e) => {
                                const newItems = [...items];
                                newItems[index].diskon2 = parseFloat(e.target.value) || 0;
                                setItems(newItems);
                                calculateItem(index, newItems);
                              }}
                              className="w-20"
                            />
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {item.total ? formatRupiah(item.total) : "-"}
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItemRow(index)}
                              className="hover:bg-red-50"
                            >
                              <X className="w-4 h-4 text-red-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Summary Section */}
              <div className="space-y-4">
                <div className="flex justify-end">
                  <div className="w-96 space-y-3 p-4 bg-gray-50 border border-gray-300 rounded-lg">
                    {/* Sub Total */}
                    {/* Total Nota */}
                    <div className="flex justify-between items-center pt-2 border-t-2 border-green-400">
                      <span className="text-lg font-bold text-green-900">TOTAL NOTA:</span>
                      <span className="text-xl font-bold text-green-700">
                        {formatRupiah(getTotalNota())}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Instruksi untuk Sales */}
                <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                  <p className="text-xs text-gray-700 leading-relaxed">
                    <strong>PENTING:</strong> Sales wajib membuat Nota ini sesuai dengan Nota manual/yang diakui pihak Toko/yang dibayar oleh customer. 
                    Jika timbul selisih, harap info via WA ke admin.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                >
                  {editMode ? "Perbarui Nota" : "Simpan Nota"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Detail Nota */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle className="text-xl">Detail Nota: {selectedNota?.nomor_nota}</DialogTitle>
            </DialogHeader>
            {selectedNota && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Tanggal</p>
                    <p className="font-semibold">{selectedNota.tanggal}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Counter</p>
                    <p className="font-semibold">{selectedNota.nama_counter}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Sales</p>
                    <p className="font-semibold">{selectedNota.nama_sales}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Jumlah Item</p>
                    <p className="font-semibold">{selectedNota.items?.length || 0} item</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Items:</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Barcode</TableHead>
                        <TableHead>Nama Barang</TableHead>
                        <TableHead>Satuan</TableHead>
                        <TableHead className="text-right">Harga</TableHead>
                        <TableHead className="text-center">Qty</TableHead>
                        <TableHead className="text-right">Disc1</TableHead>
                        <TableHead className="text-right">Disc2</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedNota.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-xs">{item.barcode}</TableCell>
                          <TableCell>{item.nama_barang}</TableCell>
                          <TableCell>{item.satuan}</TableCell>
                          <TableCell className="text-right">{formatRupiah(item.harga_satuan)}</TableCell>
                          <TableCell className="text-center font-semibold">{item.jumlah}</TableCell>
                          <TableCell className="text-right text-orange-600">{item.diskon1}%</TableCell>
                          <TableCell className="text-right text-orange-600">{item.diskon2}%</TableCell>
                          <TableCell className="text-right font-semibold">{formatRupiah(item.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-end p-4 bg-green-50 rounded-lg">
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total Nota</p>
                    <p className="text-2xl font-bold text-green-700">{formatRupiah(selectedNota.total_nota)}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default InputNota;
